import React, { useState } from 'react';
import {
  Box,
  CircularProgress,
  Card,
  CardHeader,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableContainer,
  TableRow,
  TextField,
  Typography,
  Button,
  Grid,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import dayjs from 'dayjs';
import useSWR from 'swr';
import axios from 'axios';
import { useSelector } from 'react-redux';
import ExcelJS from 'exceljs';
import { EXPORT_COLORS } from '../utils/exportHelpers';
import { apiHost } from '../utils/apiHost';

const fetcher = (url) => axios.get(url, { withCredentials: true }).then((res) => res.data);

const getApiBaseUrl = () => {
  const protocol = window.location.protocol === 'https:' ? 'https' : 'http';
  const baseUrl = apiHost().replace(/^https?:\/\//, '');
  return `${protocol}://${baseUrl}`;
};

export const Invoice = () => {
  const { user } = useSelector((state) => state.auth);
  const [selectedMonth, setSelectedMonth] = useState(dayjs().format('YYYY-MM'));
  const [selectedCabang, setSelectedCabang] = useState('');

  const { data: cabangList } = useSWR(
    user?.role === 'superadmin' ? `${getApiBaseUrl()}/cabang` : null,
    fetcher
  );

  const apiUrl = `${getApiBaseUrl()}/laporan?month=${selectedMonth}${selectedCabang ? `&cabanguuid=${selectedCabang}` : ''}`;
  const { data, error } = useSWR(apiUrl, fetcher);

  if (error) return <div>Error loading data.</div>;
  if (!data) return <CircularProgress />;

  const detailPenjualan = data?.data?.detailPenjualan || {};
  const totalPenjualanKeseluruhan = data?.data?.totalPenjualanKeseluruhan || 0;
  const totalPembayaranQRIS = data?.data?.totalPembayaranQRIS || 0;
  const totalPembayaranCash = data?.data?.totalPembayaranCash || 0;
  const totalPembayaranNonCash = data?.data?.totalPembayaranNonCash || 0;

  const handleMonthChange = (event) => {
    setSelectedMonth(event.target.value);
  };
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR'
    }).format(amount);
  };


  const getTotalTerjual = (dataCabang) =>
    Object.values(dataCabang.barang).reduce((total, barang) => total + barang.totalTerjual, 0);

  const getTotalPenjualan = (dataCabang) =>
    Object.values(dataCabang.barang).reduce((total, barang) => total + barang.totalPenjualan, 0);

  const getGrandTotalTerjual = (data) =>
    Object.values(data).reduce((total, cabang) => total + getTotalTerjual(cabang), 0);

  const getGrandTotalPenjualan = (data) =>
    Object.values(data).reduce((total, cabang) => total + getTotalPenjualan(cabang), 0);

  const exportToExcel = async () => {
    const workbook = new ExcelJS.Workbook();
    const sheet = workbook.addWorksheet('Laporan Penjualan');
    const colCount = 9; // Cabang, Barang, Kategori, Harga Satuan, Total Terjual, Total Penjualan, QRIS, Cash, Non Cash

    let r = 1;
    sheet.mergeCells(r, 1, r, colCount);
    sheet.getCell(r, 1).value = `LAPORAN PENJUALAN BULAN ${selectedMonth}`;
    sheet.getCell(r, 1).font = { bold: true, size: 14 };
    r += 2;

    const styleHeaderRow = (rowIdx) => {
      for (let c = 1; c <= colCount; c++) {
        const cell = sheet.getCell(rowIdx, c);
        cell.font = { bold: true, color: { argb: EXPORT_COLORS.headerFontColor } };
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: EXPORT_COLORS.headerFill } };
      }
    };
    const styleSubtotalRow = (rowIdx) => {
      for (let c = 1; c <= colCount; c++) {
        const cell = sheet.getCell(rowIdx, c);
        cell.font = { bold: true };
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: EXPORT_COLORS.subtotalFill } };
      }
    };

    Object.entries(detailPenjualan).forEach(([cabang, dataCabang]) => {
      // Branch title
      sheet.mergeCells(r, 1, r, colCount);
      sheet.getCell(r, 1).value = `Cabang: ${cabang}`;
      styleSubtotalRow(r);
      r += 1;

      // Payment method summary
      sheet.getCell(r, 1).value = 'Metode Pembayaran:';
      sheet.getCell(r, 7).value = `QRIS: ${dataCabang.metodePembayaran.qris.count} transaksi`;
      sheet.getCell(r, 8).value = `Cash: ${dataCabang.metodePembayaran.cash.count} transaksi`;
      sheet.getCell(r, 9).value = `Non Cash: ${dataCabang.metodePembayaran.noncash?.count || 0} transaksi`;
      r += 1;
      sheet.getCell(r, 1).value = 'Total per Metode:';
      sheet.getCell(r, 7).value = dataCabang.metodePembayaran.qris.total;
      sheet.getCell(r, 8).value = dataCabang.metodePembayaran.cash.total;
      sheet.getCell(r, 9).value = dataCabang.metodePembayaran.noncash?.total || 0;
      r += 2;

      // Item table header
      const headerRow = r;
      ['Cabang', 'Nama Barang', 'Kategori', 'Harga Satuan', 'Total Terjual', 'Total Penjualan', 'QRIS', 'Cash', 'Non Cash']
        .forEach((h, i) => { sheet.getCell(headerRow, i + 1).value = h; });
      styleHeaderRow(headerRow);
      r += 1;

      Object.entries(dataCabang.barang).forEach(([namaBarang, barangData], idx) => {
        sheet.getCell(r, 1).value = cabang;
        sheet.getCell(r, 2).value = namaBarang;
        sheet.getCell(r, 3).value = barangData.kategori;
        sheet.getCell(r, 4).value = barangData.hargaSatuan;
        sheet.getCell(r, 5).value = barangData.totalTerjual;
        sheet.getCell(r, 6).value = barangData.totalPenjualan;
        if (idx % 2 === 1) {
          for (let c = 1; c <= colCount; c++) {
            sheet.getCell(r, c).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: EXPORT_COLORS.stripeFill } };
          }
        }
        r += 1;
      });

      // Branch total
      sheet.getCell(r, 1).value = `Total ${cabang}`;
      sheet.getCell(r, 5).value = getTotalTerjual(dataCabang);
      sheet.getCell(r, 6).value = getTotalPenjualan(dataCabang);
      styleSubtotalRow(r);
      r += 2;
    });

    // Grand totals
    sheet.getCell(r, 1).value = 'GRAND TOTAL';
    sheet.getCell(r, 5).value = getGrandTotalTerjual(detailPenjualan);
    sheet.getCell(r, 6).value = getGrandTotalPenjualan(detailPenjualan);
    styleSubtotalRow(r);
    r += 1;
    sheet.getCell(r, 1).value = 'Total QRIS';
    sheet.getCell(r, 7).value = totalPembayaranQRIS;
    r += 1;
    sheet.getCell(r, 1).value = 'Total Cash';
    sheet.getCell(r, 8).value = totalPembayaranCash;
    r += 1;
    sheet.getCell(r, 1).value = 'Total Non Cash';
    sheet.getCell(r, 9).value = totalPembayaranNonCash;

    sheet.columns = [
      { width: 22 }, { width: 32 }, { width: 18 }, { width: 14 },
      { width: 14 }, { width: 16 }, { width: 22 }, { width: 22 }, { width: 22 },
    ];

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `Laporan_Penjualan_${selectedMonth}.xlsx`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  const exportCabangToExcel = async () => {
    try {
      const response = await axios.get(`${getApiBaseUrl()}/export-laporan?month=${selectedMonth}`, {
        withCredentials: true,
        responseType: 'blob'
      });
  
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Laporan_Penjualan_${selectedMonth}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Error exporting to Excel:', error);
    }
  };
  return (
    <Box>
      <Card>
        <CardHeader title="Laporan Penjualan per Cabang" />
        <Box p={2} display="flex" gap={2} flexWrap="wrap">
          <TextField
            label="Pilih Bulan"
            type="month"
            value={selectedMonth}
            onChange={handleMonthChange}
            sx={{ flex: 1, minWidth: 200 }}
          />
          {user?.role === 'superadmin' && (
            <FormControl sx={{ flex: 1, minWidth: 200 }}>
              <InputLabel>Filter Cabang</InputLabel>
              <Select
                value={selectedCabang}
                onChange={(e) => setSelectedCabang(e.target.value)}
                label="Filter Cabang"
              >
                <MenuItem value="">
                  <em>Semua Cabang</em>
                </MenuItem>
                {cabangList?.data?.map((cabang) => (
                  <MenuItem key={cabang.uuid} value={cabang.uuid}>
                    {cabang.namacabang}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </Box>
        <Box p={2} display="flex" justifyContent="flex-end">
          <Button variant="contained" color="primary" onClick={exportToExcel}>
            Export to Excel
          </Button>
        </Box>

        {/* Summary Cards */}
        <Box p={2}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <Paper sx={{ p: 2, backgroundColor: '#f5f5f5' }}>
                <Typography variant="h6" gutterBottom>
                  Total Penjualan
                </Typography>
                <Typography variant="h4">
                  {formatCurrency(totalPenjualanKeseluruhan)}
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} md={4}>
              <Paper sx={{ p: 2, backgroundColor: '#e8f5e9' }}>
                <Typography variant="h6" gutterBottom>
                  Total QRIS
                </Typography>
                <Typography variant="h4">
                  {formatCurrency(totalPembayaranQRIS)}
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} md={4}>
              <Paper sx={{ p: 2, backgroundColor: '#fce4ec' }}>
                <Typography variant="h6" gutterBottom>
                  Total Cash
                </Typography>
                <Typography variant="h4">
                  {formatCurrency(totalPembayaranCash)}
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </Box>

        {/* Data Table dengan Scroll untuk Mobile */}
        {Object.keys(detailPenjualan).length > 0 ? (
          Object.entries(detailPenjualan).map(([cabang, dataCabang]) => (
            <Box key={cabang} mb={4}>
              <Typography variant="h6" sx={{ p: 2 }}>
                Cabang: {cabang}
              </Typography>

              {/* Scroll Table */}
              <TableContainer sx={{ maxWidth: "100%", overflowX: "auto" }}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Nama Barang</TableCell>
                      <TableCell>Kategori</TableCell>
                      <TableCell>Harga Satuan</TableCell>
                      <TableCell>Total Terjual</TableCell>
                      <TableCell>Total Penjualan</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {Object.entries(dataCabang.barang || {}).map(([namaBarang, barangData]) => (
                      <TableRow key={namaBarang}>
                        <TableCell>{namaBarang}</TableCell>
                        <TableCell>{barangData.kategori}</TableCell>
                        <TableCell>{barangData.hargaSatuan}</TableCell>
                        <TableCell>{barangData.totalTerjual}</TableCell>
                        <TableCell>{barangData.totalPenjualan}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          ))
        ) : (
          <Box p={2} textAlign="center">
            <p>Data tidak ditemukan untuk bulan yang dipilih.</p>
          </Box>
        )}
      </Card>
    </Box>
  );
};
